import { useState, useEffect } from 'react';
import { IProduct, addProduct, updateProduct, deleteProduct } from '../../../../libraries/helpdesk/product-management';
import { Language } from '../../../languages/languageHandler';
import ModalComponent from '../../../components/modal';
import CustomModal from '../../../components/custom-modal';
import { Modal, Grid, GridCol, TextInput, Checkbox, Button, Group, NumberInput } from '@mantine/core';
import { ICurrency } from '../../../../libraries/currencies';

interface ModalProps {
  mode: string;
  isOpen: boolean;
  hideBtn: VoidFunction;
  lang: Language;
  product: IProduct;
  currency: ICurrency;
}

const ProductModal = ({ mode, isOpen, hideBtn, lang, product, currency }: ModalProps) => {
  const [loading, setLoading] = useState(false);
  const [productCode, setProductCode] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [um, setUm] = useState<string>('');
  const [hasComposition, setHasComposition] = useState<boolean>(false);
  const [hasSn, setHasSn] = useState<boolean>(false);
  const [priceRef, setPriceRef] = useState<number>(0);
  const [lastSupplier, setLastSupplier] = useState<number>(0);
  const [lastPrice, setLastPrice] = useState<number>(0);
  const [ceCertificate, setCeCertificate] = useState<boolean>(false);
  const [cbCertificate, setCbCertificate] = useState<boolean>(false);
  const [averDeliTime, setAverDeliTime] = useState<number>(0);
  const [devType, setDevType] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [cType, setCType] = useState<number>(0);

  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (product) {
      setProductCode(product.productCode || '');
      setDescription(product.description || '');
      setUm(product.um || '');
      setHasComposition(product.hasComposition || false);
      setHasSn(product.hasSN || false);
      setPriceRef(Number((product.priceRef * currency.factor).toFixed(2)) || 0);
      setLastSupplier(product.lastSupplier || 0);
      setLastPrice(Number((product.lastPrice * currency.factor).toFixed(2)) || 0);
      setCeCertificate(product.ceCertificate || false);
      setCbCertificate(product.cbCertificate || false);
      setAverDeliTime(product.averDeliTime || 0);
      setDevType(product.devType || 0);
      setPrice(Number((product.price * currency.factor).toFixed(2)) || 0);
      setCType(product.ctype || 0);
    } else {
      setProductCode('');
      setDescription('');
      setUm('');
      setHasComposition(false);
      setHasSn(false);
      setPriceRef(0);
      setLastSupplier(0);
      setLastPrice(0);
      setCeCertificate(false);
      setCbCertificate(false);
      setAverDeliTime(0);
      setDevType(0);
      setPrice(0);
      setCType(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, mode]);

  const handleTitle = (currentMode: string) => {
    switch (currentMode) {
      case 'read':
        return `${product?.productCode || ''} - ${product?.description || ''}`;
      case 'new':
        return lang.PRODUCT_ADD_PRODUCT || 'Add Product';
      case 'edit':
        return `${product?.productCode || ''} - ${product?.description || ''}`;
      default:
        return '';
    }
  };

  const handleSave = async () => {
    setLoading(true);
    const controller = new AbortController();

    try {
      let response;
      const productData: IProduct = {
        productCode,
        description,
        um,
        hasComposition,
        hasSN: hasSn,
        priceRef: priceRef / currency.factor,
        lastSupplier,
        lastPrice: lastPrice / currency.factor,
        ceCertificate,
        cbCertificate,
        averDeliTime,
        devType,
        price,
        ctype: cType
      };

      if (mode === 'new') {
        response = await addProduct(productData, controller);
      } else if (mode === 'edit') {
        response = await updateProduct(productData, controller);
      } else if (mode === 'delete') {
        response = await deleteProduct(product.productCode, controller);
      }

      if (!response || response.statusOper?.code !== 0) {
        const code = response?.statusOper?.code ?? 'UNKNOWN';
        const message = response?.statusOper?.message ?? 'Unexpected error';

        const baseError =
          mode === 'new'
            ? lang.ADD_PRODUCT_ERROR
            : mode === 'edit'
              ? lang.EDIT_PRODUCT_ERROR
              : lang.DELETE_PRODUCT_ERROR;

        setErrorMessage(`${baseError} - ${code}, ${message}`);
        setShowErrorDialog(true);
        setTimeout(() => setShowErrorDialog(false), 2000);
      } else {
        const successMsg =
          mode === 'new'
            ? lang.ADD_PRODUCT_SUCCESS
            : mode === 'edit'
              ? lang.EDIT_PRODUCT_SUCCESS
              : lang.DELETE_PRODUCT_SUCCESS;

        setSuccessMessage(successMsg);
        setShowSuccessDialog(true);
        setTimeout(() => {
          setShowSuccessDialog(false);
          hideBtn();
        }, 2000);
      }
    } catch (error: any) {
      const baseError =
        mode === 'new' ? lang.ADD_PRODUCT_ERROR : mode === 'edit' ? lang.EDIT_PRODUCT_ERROR : lang.DELETE_PRODUCT_ERROR;

      const code = error?.statusOper?.code || error?.code || 'EXCEPTION';
      const message = error?.statusOper?.message || error?.message || 'An unexpected error occurred';

      setErrorMessage(`${baseError} - ${code}, ${message}`);
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    const controller = new AbortController();
    try {
      const response = await deleteProduct(product.productCode, controller);
      if (!response || response.statusOper?.code !== 0) {
        const code = response?.statusOper?.code ?? 'UNKNOWN';
        const message = response?.statusOper?.message ?? 'Unexpected error';
        setErrorMessage(`${lang.DELETE_PRODUCT_ERROR} - ${code}, ${message}`);
        setShowErrorDialog(true);
        setTimeout(() => setShowErrorDialog(false), 2000);
      } else {
        setSuccessMessage(lang.DELETE_PRODUCT_SUCCESS);
        setShowSuccessDialog(true);
        setTimeout(() => {
          setShowSuccessDialog(false);
          hideBtn();
        }, 2000);
      }
    } catch (error: any) {
      const code = error?.statusOper?.code || error?.code || 'EXCEPTION';
      const message = error?.statusOper?.message || error?.message || 'An unexpected error occurred';
      setErrorMessage(`${lang.DELETE_PRODUCT_ERROR} - ${code}, ${message}`);
      setShowErrorDialog(true);
      setTimeout(() => setShowErrorDialog(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalComponent
        show={isOpen}
        handleClick={hideBtn}
        title={handleTitle(mode)}
        content={
          <Modal.Body style={{ margin: '0', padding: '0' }}>
            <Grid>
              <GridCol span={{ base: 12, sm: 6 }}>
                <TextInput
                  label={lang.HELPDESK_PRODUCT_CODE}
                  value={productCode}
                  onChange={e => setProductCode(e.target.value)}
                  disabled={mode === 'read' || mode === 'edit'}
                />
              </GridCol>
              <GridCol span={{ base: 12, sm: 6 }}>
                <TextInput
                  label={lang.EQUIPMENT_DESCRIPTION}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  disabled={mode === 'read'}
                />
              </GridCol>
              <GridCol span={{ base: 12, sm: 6 }}>
                <TextInput label="UM" value={um} onChange={e => setUm(e.target.value)} disabled={mode === 'read'} />
              </GridCol>
              <GridCol span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label={lang.PRICE_REFERENCE}
                  value={priceRef}
                  onChange={value => setPriceRef(value as number)}
                  disabled={mode === 'read'}
                  hideControls
                  suffix={` ${currency.currencyStr}`}
                />
              </GridCol>
              {(mode === 'read' || mode === 'edit') && (
                <GridCol span={{ base: 12, sm: 6 }}>
                  <NumberInput
                    label={lang.LAST_SUPPLIER}
                    value={lastSupplier}
                    onChange={val => setLastSupplier(val as number)}
                    disabled={mode === 'read'}
                    hideControls
                  />
                </GridCol>
              )}
              {(mode === 'read' || mode === 'edit') && (
                <GridCol span={{ base: 12, sm: 6 }}>
                  <NumberInput
                    label={lang.LAST_PRICE}
                    value={lastPrice}
                    onChange={val => setLastPrice(val as number)}
                    disabled={mode === 'read'}
                    hideControls
                    suffix={` ${currency.currencyStr}`}
                  />
                </GridCol>
              )}
              <GridCol span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label={lang.AVERAGE_DELIVERY_TIME}
                  value={averDeliTime}
                  onChange={val => setAverDeliTime(val as number)}
                  disabled={mode === 'read'}
                  hideControls
                />
              </GridCol>
              {(mode === 'read' || mode === 'edit') && (
                <GridCol span={{ base: 12, sm: 6 }}>
                  <NumberInput
                    label={lang.PRODUCT_DEV_TYPE}
                    value={devType}
                    onChange={val => setDevType(val as number)}
                    disabled={mode === 'read'}
                    hideControls
                  />
                </GridCol>
              )}
              <GridCol span={{ base: 12 }}>
                <Group grow>
                  <Checkbox
                    label="BM"
                    checked={hasComposition}
                    onChange={e => setHasComposition(e.currentTarget.checked)}
                    disabled={mode === 'read'}
                  />
                  <Checkbox
                    label={lang.EQUIPMENT_SERIAL_NUMBER || 'Has Serial Number'}
                    checked={hasSn}
                    onChange={e => setHasSn(e.currentTarget.checked)}
                    disabled={mode === 'read'}
                  />
                  <Checkbox
                    label={lang.CE_CERTIFICATE || 'CE Certificate'}
                    checked={ceCertificate}
                    onChange={e => setCeCertificate(e.currentTarget.checked)}
                    disabled={mode === 'read'}
                  />
                  <Checkbox
                    label={lang.CB_CERTIFICATE || 'CB Certificate'}
                    checked={cbCertificate}
                    onChange={e => setCbCertificate(e.currentTarget.checked)}
                    disabled={mode === 'read'}
                  />
                </Group>
              </GridCol>

              {(mode === 'edit' || mode === 'new') && (
                <Group className="buttons">
                  {mode === 'edit' && (
                    <>
                      <Button radius="md" type="button" className="save" onClick={handleSave} loading={loading}>
                        {lang.GLOBAL_SAVE_BUTTON}
                      </Button>
                      <Button radius="md" type="button" className="delete" onClick={handleDelete} loading={loading}>
                        {lang.GLOBAL_DELETE}
                      </Button>
                    </>
                  )}

                  {mode === 'new' && (
                    <Button radius="md" type="button" className="save" onClick={handleSave} loading={loading}>
                      {lang.GLOBAL_SAVE_BUTTON}
                    </Button>
                  )}
                </Group>
              )}
            </Grid>
          </Modal.Body>
        }
      />

      <CustomModal
        opened={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={successMessage}
        backgroundColor="#3BDF70"
        textColor="black"
      />

      <CustomModal
        opened={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
        backgroundColor="#E3353F"
        textColor="white"
      />
    </>
  );
};

export default ProductModal;
